import React from "react"
import makeStyles from "@mui/styles/makeStyles"
import Box from "@mui/material/Box"
import Grid from "@mui/material/Grid"
import Paper from "@mui/material/Paper"
import Typography from "@mui/material/Typography"
import {
  DataGridPro,
  useGridApiContext,
  useGridSelector,
  gridPageSelector,
  gridPageCountSelector,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarColumnsButton,
  GridToolbarExport,
} from "@mui/x-data-grid-pro"
import Pagination from "@mui/material/Pagination"

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    "& .subject": {
      fontWeight: 900,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    "& .MuiDataGrid-columnSeparator": {
      color: "transparent",
    },
    "& .Mui-even": {
      backgroundColor: theme.palette.grey[50],
    },
    "& .MuiDataGrid-row:hover": {
      background: theme.palette.grey[200],
    },
    "& .MuiDataGrid-cell:focus": {
      outline: "none !important",
    },
    "& .MuiDataGrid-footerContainer": {
      justifyContent: "center",
    },
  },
}))

const EmptyContent = ({ children }) => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    p={2}
    position="absolute"
    left={0}
    right={0}
    top={0}
    bottom={0}
  >
    {children}
  </Box>
)

const CustomToolbar = ({additionalToolbars, showExportToolbar}) => {
  return (
    <GridToolbarContainer style={{ paddingTop: 10, paddingBottom: 10 }}>
      <Grid container justifyContent="space-between">
        <Grid item>
          <GridToolbarFilterButton />
          <GridToolbarColumnsButton />
          {showExportToolbar === true
            ? <GridToolbarExport printOptions={{disableToolbarButton: true}}/>
            : null
          }
        </Grid>
        <Grid item xs={6} container justifyContent="flex-end">
          {!!additionalToolbars && additionalToolbars.length > 0 ? (
            additionalToolbars.map((ToolbarItem) => {
              return (
                <Grid>
                  <ToolbarItem />
                </Grid>
              )
            })
          ) : (
            <></>
          )}
        </Grid>
      </Grid>
    </GridToolbarContainer>
  )
}

const CustomPagination = () => {
  const apiRef = useGridApiContext()
  const page = useGridSelector(apiRef, gridPageSelector)
  const pageCount = useGridSelector(apiRef, gridPageCountSelector)

  return pageCount > 1 ? (
    <Pagination
      count={pageCount}
      size="large"
      page={page + 1}
      onChange={(event, value) => apiRef.current.setPage(value - 1)}
    />
  ) : (
    ""
  )
}

export default function DataTable({
  columns,
  noRowsContent,
  noResultsContent,
  pageSize,
  rows,
  rowsSelected,
  setRowsSelected,
  showCheckboxes,
  initialFilterModel,
  additionalToolbars,
  showExportToolbar,
  elevation,
  ...props
}) {
  const classes = useStyles()
  const style = typeof props?.style === 'object'
    ? {...props.style}
    : {}
    
  return (
    <Paper elevation={elevation ?? 1}>
      <DataGridPro
        pagination
        autoHeight
        checkboxSelection={showCheckboxes ?? true}
        className={classes.root}
        columns={columns}
        components={{
          NoRowsOverlay: () => (
            <EmptyContent>
              <Typography variant="h3">{noRowsContent || "No items to show"}</Typography>
            </EmptyContent>
          ),
          NoResultsOverlay: () => (
            <EmptyContent>
              <Typography variant="h3">{noResultsContent || "No items to show"}</Typography>
            </EmptyContent>
          ),
          Pagination: CustomPagination,
          Toolbar: CustomToolbar,
        }}
        componentsProps={{
          toolbar: {additionalToolbars, showExportToolbar},
        }}
        disableSelectionOnClick
        editMode="row"
        headerHeight={32}
        initialState={initialFilterModel ? {filter: {filterModel: initialFilterModel}} : undefined}
        pageSize={pageSize || 20}
        rowHeight={56}
        rows={Array.isArray(rows) ? rows : []}
        selectionModel={rowsSelected ?
          (Array.isArray(rowsSelected) ? rowsSelected.map((row) => row.id) : rowsSelected)
          : undefined}
        onSelectionModelChange={(ids) => {
          if (rowsSelected) {
            const selectedIDs = new Set(ids)
            setRowsSelected(rows.filter((row) => selectedIDs.has(row.id.toString())))
          }
        }}
        {...props}
        sx={style}
      />
    </Paper>
  )
}
